/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { ReactNode, useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { tooltip, trigger } from './tooltip.style';

type Position = {
  top: number;
  left: number;
};

type Placement = 'top' | 'bottom' | 'left' | 'right';

interface InfoHintProps {
  label: ReactNode;
  width?: number | string;
  openDelay?: number;
  closeDelay?: number;
  placement?: Placement;
  children: ReactNode;
}

export const InfoHint = React.memo(function InfoHint({
  label,
  width = 240,
  openDelay = 100,
  closeDelay = 100,
  placement = 'right',
  children,
}: InfoHintProps) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);

  const [pos, setPos] = useState<Position | null>(null);

  const clearTimers = () => {
    if (openTimer.current) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const show = useCallback(() => {
    clearTimers();

    openTimer.current = window.setTimeout(() => {
      const el = triggerRef.current;
      /* istanbul ignore next */
      if (!el) return;

      const rect = el.getBoundingClientRect();

      let next: Position;
      switch (placement) {
        case 'top':
          next = { top: rect.top, left: rect.left + rect.width / 2 };
          break;
        case 'bottom':
          next = { top: rect.bottom, left: rect.left + rect.width / 2 };
          break;
        case 'left':
          next = { top: rect.top + rect.height / 2, left: rect.left };
          break;
        case 'right':
        default:
          next = { top: rect.top + rect.height / 2, left: rect.right };
          break;
      }

      setPos(next);
    }, openDelay);
  }, [openDelay, placement]);

  const hide = useCallback(() => {
    clearTimers();

    closeTimer.current = window.setTimeout(() => {
      setPos(null);
    }, closeDelay);
  }, [closeDelay]);

  return (
    <React.Fragment>
      <span ref={triggerRef} css={trigger} onMouseEnter={show} onMouseLeave={hide}>
        {children}
      </span>

      {pos &&
        createPortal(
          <div css={tooltip(width, placement)} style={{ top: pos.top, left: pos.left }}>
            {label}
          </div>,
          document.body,
        )}
    </React.Fragment>
  );
});
